import * as R from 'ramda';
import React, { Fragment, useCallback } from 'react';
// components
import TextComponent from '../../../../components/text';
// helpers/constants
import * as G from '../../../../helpers';
import * as GC from '../../../../constants';
//
import { useFormikArray } from '../../../../hooks';
// ui
import { Box, Flex, MainActionButton, scrollableContainerCss4px } from '../../../../ui';
// feature fleet
import EstimateInfo from './estimate-info';
import EstimateForm from './estimate-form';
import { invoiceDefaultFields2 } from '../../equipment-service/settings/field-settings';
//////////////////////////////////////////////////

const getTotal = (estimates) => R.compose(
  R.sum,
  R.map(G.getCostsTotal),
)(estimates);

const Total = ({ ml, mr, maxWidth, totalText }) => (
  <Box ml={ml} mr={mr} p='5px' borderRadius='5px' bg='titleDarkBlue' color={G.getTheme('colors.white')}>
    <TextComponent display='block' title={totalText} withEllipsis={true} maxWidth={maxWidth}>
      {totalText}
    </TextComponent>
  </Box>
);

// TODO: check normalized total
const Totals = ({ values, estimates }) => {
  const currencySymbol = G.getCurrencySymbol(G.getCurrencyFromObject(values));

  const total = getTotal(estimates);

  return (
    <Flex width='100%' p='5px 15px' justifyContent='center' bg={G.getTheme('colors.whiteGrey')}>
      <Total
        ml={10}
        maxWidth={150}
        totalText={`${G.getWindowLocale('titles:total', 'Total')}: ${currencySymbol} ${total}`}
      />
    </Flex>
  );
};

const EstimatesTab = (props) => {
  const {
    values,
    openModal,
    closeModal,
    setFieldValue,
    serviceVendorListAvailableOptions,
  } = props;

  const { invoices } = values;

  const { add, update, remove, array: estimates } = useFormikArray(
    GC.FIELD_SERVICE_VENDOR_ESTIMATES, values, setFieldValue,
  );

  const addEstimate = useCallback((
    item,
  ) => {
    add(item);

    closeModal();
  }, [values]);

  const updateEstimate = useCallback((
    item,
    estimateIndex,
  ) => {
    update(estimateIndex, item);

    closeModal();
  }, [values]);

  const createInvoice = useCallback((
    estimate,
  ) => {
    const { fees, parts, labors, currency, description, serviceVendorGuid } = estimate;

    const invoice = {
      ...invoiceDefaultFields2,
      currency,
      serviceVendorGuid,
      [GC.FIELD_COMMENTS]: description,
      [GC.FIELD_FEES]: R.map(G.omitObjectSystemFields, R.or(fees, [])),
      [GC.FIELD_PARTS]: R.map(G.omitObjectSystemFields, R.or(parts, [])),
      [GC.FIELD_LABORS]: R.map(G.omitObjectSystemFields, R.or(labors, [])),
    };

    setFieldValue(
      GC.FIELD_INVOICES, [...invoices, invoice],
    );
  }, [values]);

  const handleAddOrEditEstimate = useCallback((estimate, estimateIndex) => {
    const isEditMode = G.isNotNilAndNotEmpty(estimate);

    const component = (
      <EstimateForm
        isEditMode={isEditMode}
        initialValues={estimate}
        serviceVendorListAvailableOptions={serviceVendorListAvailableOptions}
        submitAction={(values) => {
          if (isEditMode) return updateEstimate(values, estimateIndex);

          addEstimate(values);
        }}
      />
    );

    const title = G.ifElse(
      isEditMode,
      G.getEditTitle,
      G.getAddTitle,
    )(['titles:estimate', 'Estimate']);

    const modal = {
      p: '0px',
      component,
      options: { title, width: 780 },
    };

    openModal(modal);
  }, [values, serviceVendorListAvailableOptions]);

  return (
    <Fragment>
      <Totals estimates={R.or(estimates, [])} />
      <Box
        mr={10}
        pl={15}
        pr='5px'
        overflowY='auto'
        height='calc(100% - 170px)'
        css={scrollableContainerCss4px}
      >
        {
          R.or(estimates, []).map((estimate, index) => (
            <EstimateInfo
              estimate={estimate}
              openModal={openModal}
              closeModal={closeModal}
              createInvoice={createInvoice}
              handleRemoveEstimate={() => remove(index)}
              key={`${index}-${G.getGuidFromObject(estimate)}`}
              serviceVendorListAvailableOptions={serviceVendorListAvailableOptions}
              handleAddOrEditEstimate={(values) => handleAddOrEditEstimate(values, index)}
            />
          ))
        }
      </Box>
      <Box my={15} px={15}>
        <MainActionButton
          height={32}
          width='100%'
          type='button'
          onClick={() => handleAddOrEditEstimate()}
        >
          {G.getAddTitle(['titles:estimate', 'Estimate'])}
        </MainActionButton>
      </Box>
    </Fragment>
  );
};

export default EstimatesTab;
